import { useLayoutEffect, useMemo } from "react";
import type { WbeTheme } from "@api-contracts/sites/wbe/theme";
import {
  DEFAULT_THEME_CHANNELS,
  SQUARE_BADGE_RADIUS,
  type ThemeToken,
} from "./defaults";
import { deriveTheme, type DerivedTheme } from "./deriveTheme";

const TOKENS = Object.keys(DEFAULT_THEME_CHANNELS) as ThemeToken[];

/** Writes the derived variables onto `target`, or clears them so the defaults apply again. */
export const applyTheme = (
  target: HTMLElement,
  theme: DerivedTheme | null,
  roundedCorners = true,
) => {
  for (const token of TOKENS) {
    if (theme)
      target.style.setProperty(`--wbe-${token}`, theme.channels[token]);
    else target.style.removeProperty(`--wbe-${token}`);
  }
  if (theme) {
    target.style.setProperty("--wbe-scrim-alpha", theme.scrimAlpha);
    target.style.setProperty("color-scheme", theme.isDark ? "dark" : "light");
  } else {
    target.style.removeProperty("--wbe-scrim-alpha");
    target.style.removeProperty("color-scheme");
  }
  if (theme && !roundedCorners) {
    target.style.setProperty("--wbe-radius-scale", "0");
    target.style.setProperty("--wbe-radius-badge", SQUARE_BADGE_RADIUS);
  } else {
    target.style.removeProperty("--wbe-radius-scale");
    target.style.removeProperty("--wbe-radius-badge");
  }
};

/** Keeps `targets` painted with `theme` for as long as the caller is mounted. */
export const useAppliedTheme = (
  targets: (HTMLElement | null | undefined)[],
  theme: WbeTheme | null,
) => {
  const key = theme
    ? `${theme.backgroundColor},${theme.primaryColor},${theme.accentColor},${theme.roundedCorners}`
    : "";
  const roundedCorners = theme?.roundedCorners ?? true;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const derived = useMemo(() => (theme ? deriveTheme(theme) : null), [key]);
  useLayoutEffect(() => {
    const live = targets.filter((t): t is HTMLElement => !!t);
    live.forEach((target) => applyTheme(target, derived, roundedCorners));
    return () => live.forEach((target) => applyTheme(target, null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [derived, ...targets]);
};

/** The site's theme, or the unsaved preview over it. */
export const siteTheme = (
  config: { theme?: WbeTheme | null } | null | undefined,
  preview: WbeTheme | null = null,
): WbeTheme | null => preview ?? config?.theme ?? null;
